<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";

import SvgIcon from "@/components/ui/SvgIcon/SvgIcon.vue";
import type { Iteration } from "@/model/session";
import { useSessionStore } from "@/store/session";
import { toShortDateString } from "@/utils/dateFormat";

interface Props {
  iteration: Iteration;
}

const props = defineProps<Props>();

const { t } = useI18n();

const currentDate = computed(() => toShortDateString(new Date()));

const iterationInfo = computed(() => {
  const iterationNow = useSessionStore().iterationProgress(new Date(), props.iteration);
  const passed = iterationNow.iterationsPassed;

  if (passed <= 0) {
    return t("statusDistribution.iteration.notStarted");
  }
  if (passed >= 1) {
    return t("statusDistribution.iteration.ended");
  }
  return t("statusDistribution.iteration", {
    day: Math.ceil(iterationNow.daysPassedInIteration),
    total: iterationNow.daysInIteration,
  });
});
</script>

<template>
  <div class="date-banner">
    <div class="date-with-icon">
      <SvgIcon name="general/calendar" width="16" height="16" />
      <span>{{ t("statusDistribution.today", { today: currentDate }) }}</span>
    </div>
    <span>{{ iterationInfo }}</span>
  </div>
</template>

<style lang="scss" scoped>
@use "@/styles/mixins/typography";

.date-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px;
  background: transparent;
  border-radius: 4px;
  height: 30px;

  @include typography.small("semi-bold");

  color: currentcolor;
}

.date-with-icon {
  display: flex;
  align-items: center;
  gap: 4px;
}
</style>
